import { EffectsImage } from '@/components/video-effects/EffectsImage';
import { mapSourceTimeToClipTime } from '@/lib/video-effects/sections';
import { VideoEffectsProvider } from '@/components/video-effects/VideoEffectsContext';
import { AbsoluteFill, Sequence, useVideoConfig, useCurrentFrame } from "remotion";
import { hexToRgba } from '@/lib/layers/hexToRgba';
import { useCallback, useMemo } from "react";
import Subtitle from "@/components/remotion/Subtitle";
import Logo from "@/components/remotion/Logo";
import { ClipWatermark } from "@/components/remotion/watermark/ClipWatermark";
import type { WatermarkPosition } from "@/lib/watermark/watermarkPosition";
import LayerOverlay from "@/components/remotion/LayerOverlay";
import { useSourceAspectRatio } from "@/remotion/useSourceAspectRatio";
import lodash from 'lodash';
import { buildSubtitleReorganizeOptions } from '@/lib/subtitles/buildSubtitleReorganizeOptions';
import reorganizeWords from '@/lib/reorganize-words';
import type { WatermarkConfig } from '@/lib/campaign-watermark-types';
import { POST_LAYOUTS } from '@/lib/layouts/registry';
import { circuloCurveFromLayout } from '@/lib/layouts/resolveCirculoPreset';
import { resolveLayoutClipCopyMode, type LayoutClipCopyHost } from '@/lib/layouts/resolveLayoutClipCopyMode';
import { resolveMediaMode } from '@/lib/layouts/resolveMediaMode';
import { ClipVideoSequences } from '@/components/layouts/ClipVideoSequences';
import { ClipPlaybackVideo } from '@/components/preview/ClipPlaybackVideo';
import { EditorPlaybackStack } from '@/components/preview/EditorPlaybackStack';
import { EditorPlaybackVideo } from '@/components/preview/EditorPlaybackVideo';
import { resolveEditorPlaybackFrame } from '@/lib/preview/resolveEditorPlaybackFrame';
import { ClipAudioSequences } from '@/components/layouts/ClipAudioSequences';
import { StaticImageMedia } from '@/components/layouts/StaticImageMedia';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { buildSceneLayout } from '@/lib/transitions/buildSceneLayout';
import { sourceTimeToOutputTime, outputFrameToSourceFrame } from '@/lib/transitions/mapTimes';
import { resolveCameraMoveCrop } from '@/lib/transitions/resolveCameraMoveCrop';
import { computeCropWrapperBox } from '@/lib/crop/computeCropWrapperBox';
import { sceneAudioFades, makeSceneVolume } from '@/lib/transitions/resolveSceneVolume';
import { SceneTransitionWrapper, TransitionDipOverlays, KeyframeTransitionStyles } from '@/components/remotion/SceneTransitions';
import { deriveKeyframeCutWindows } from '@/lib/transitions/keyframeCuts';
import { resolveCropKeyframeAtTime } from '@/lib/crop/resolveCropKeyframeAtTime';
import { subtitleOutlineLineStyleFromPartial, type SubtitleShadowStyle } from '@/lib/subtitles/subtitleOutline';
import type { SceneTransition, KeyframeTransition } from '@/lib/transitions/types';

interface Word {
  word: string;
  start: number;
  end: number;
  start_time?: number;
  end_time?: number;
  startTime?: number;
  endTime?: number;
  isWithinInterval?: boolean;
  gradientOpacity?: number;
}

interface Segment {
  words: Word[];
  startTime: number;
  endTime: number;
  intervalStartTime: number;
}

interface PresetChange {
  type: 'set-value' | 'reorganize-words';
  attribute?: string;
  value?: any;
  settings?: any;
}

interface SubtitlePreset {
  id: string;
  name: string;
  preview: string;
  preset: PresetChange[];
  editable: boolean;
  default: boolean;
}


export interface LogoConfig {
  url?: string;
  position?: string;
  size?: number;
  opacity?: number;
  horizontalMargin?: number;
  verticalMargin?: number;
}

/** Kept source intervals: the explicit list, else the single start/end range. */
function resolveKeptIntervals(
  intervals: ReadonlyArray<{ startTime: number; endTime: number }> | undefined,
  startTime: number | undefined,
  endTime: number | undefined,
): Array<{ startTime: number; endTime: number }> {
  if (intervals && intervals.length > 0) return intervals as Array<{ startTime: number; endTime: number }>;
  return typeof startTime === 'number' && typeof endTime === 'number' ? [{ startTime, endTime }] : [];
}

const ProjectClipCompositionInner: React.FC<{
  videoUrl: string;
  startTime?: number;
  endTime?: number;
  intervals?: Array<{ startTime: number; endTime: number }>;
  isMuted: boolean;
  volume: number;
  style?: React.CSSProperties;
  subtitlesEnabled?: boolean;
  subtitleStyle?: string;
  transcriptionSegments?: Segment[][];
  selectedSubtitlePreset?: SubtitlePreset;
  cropCenterKeyframes?: Array<{ time: number; endTime: number; x: number; y: number }>;
  activeFaces?: Array<{ face_id: string; x: number; y: number; width: number; height: number; is_speaking?: boolean; is_main_speaker?: boolean }>;
  speakerColors?: Record<string, string>;
  speakerLayout?: {
    numSpeakers: number;
    speakers: Array<{
      face_id: string;
      scenes: Array<{ start: number; end: number; cropCenter: { x: number; y: number } }>;
    }>;
    sceneTimeline?: Array<{ start: number; end: number; faceIds: string[] }>;
  };
  subtitleConfig?: {
    fontFamily?: string;
    fontUrl?: string | null;
    fontSize?: number;
    textColor?: string;
    highlightColor?: string;
    highlightEnabled?: boolean;
    highlightBoxEnabled?: boolean;
    highlightBoxBackgroundColor?: string;
    highlightBoxBorderRadius?: number;
    highlightBoxPaddingX?: number;
    highlightBoxPaddingY?: number;
    backgroundColor?: string;
    backgroundEnabled?: boolean;
    backgroundOpacity?: number;
    backgroundWidth?: number;
    backgroundHeight?: number;
    position?: string;
    marginVertical?: number;
    marginHorizontal?: number;
    shadowEnabled?: boolean;
    shadowColor?: string;
    shadowSize?: number;
    strokeEnabled?: boolean;
    strokeColor?: string;
    strokeWidth?: number;
    shadowStyle?: SubtitleShadowStyle;
    shadowOffsetX?: number;
    shadowOffsetY?: number;
    shadowSpread?: number;
    animation?: string;
    textAlign?: string;
    textTransform?: string;
    lineSpacing?: number;
    animationSpeed?: number;
    maxWordsPerLine?: number;
    removePunctuation?: boolean;
  };
  isVideoLocked?: boolean;
  fullVideoDuration?: number;
  logoConfig?: LogoConfig;
  showSafeZone?: boolean;
  showVolumeControls?: boolean;
  showWatermark?: boolean;
  campaignWatermark?: WatermarkConfig;
  watermarkPosition?: WatermarkPosition | null;
  postLayout?: PostLayoutConfig | null;
  layers?: import('@/lib/layers/types').LayerOverlayItem[];
  transitions?: SceneTransition[];
  videoEffects?: unknown;
  keyframeTransitions?: KeyframeTransition[];
  posterUrl?: string | null;
  playback?: 'html5' | 'media';
  /** Editor Player uses preview stills; BrandKitCard MP4s use export live copies. */
  layoutClipCopyHost?: LayoutClipCopyHost;
  onMediaError?: (error: Error) => void;
  previewPlaying?: boolean;
  onPreviewBuffering?: (buffering: boolean) => void;
  onPreviewMediaReady?: () => void;
}> = ({ videoUrl, startTime, endTime, intervals, isMuted, volume, style, subtitlesEnabled, transcriptionSegments, selectedSubtitlePreset, cropCenterKeyframes, activeFaces, speakerLayout, subtitleConfig: customSubtitleConfig, isVideoLocked, fullVideoDuration, logoConfig, showSafeZone, showWatermark, campaignWatermark, watermarkPosition, postLayout, layers, transitions, keyframeTransitions, posterUrl, playback = 'html5', layoutClipCopyHost = 'preview', onMediaError, previewPlaying = false, onPreviewBuffering, onPreviewMediaReady }) => {
  const { fps, width: compWidth, height: compHeight } = useVideoConfig();
  const frame = useCurrentFrame();
  const sourceAspectRatio = useSourceAspectRatio(videoUrl);

  // All layouts must share the Player's time axis. Full-video mode places the
  // entire source at frame zero; Clip mode concatenates the selected scenes.
  const usesFullVideoAxis = !isVideoLocked && !!fullVideoDuration;
  const previewTransitions = usesFullVideoAxis ? undefined : transitions;
  const keptIntervals = useMemo(
    () => resolveKeptIntervals(intervals, startTime, endTime),
    [intervals, startTime, endTime],
  );
  const sceneItems = useMemo(() => (
    usesFullVideoAxis ? [{ startTime: 0, endTime: fullVideoDuration! }] : keptIntervals
  ), [keptIntervals, usesFullVideoAxis, fullVideoDuration]);

  const sceneLayout = useMemo(
    () => buildSceneLayout({ scenes: sceneItems, transitions: previewTransitions ?? [], fps }),
    [sceneItems, previewTransitions, fps],
  );

  // Keyframe-boundary transitions map onto the locked-video output axis (the
  // same axis the block map lives on), strictly inside a block so they never
  // straddle an interval edge. Empty ⇒ no wrapping, byte-identical output.
  const keyframeCuts = useMemo(
    () => deriveKeyframeCutWindows({
      transitions: usesFullVideoAxis ? [] : keyframeTransitions ?? [],
      scenes: sceneItems,
      blocks: sceneLayout.blocks,
      fps,
    }),
    [keyframeTransitions, sceneItems, sceneLayout, fps, usesFullVideoAxis],
  );

  // Compute crop position from scene intervals for portrait mode
  // Matches worker logic: one crop center per scene interval, find which interval
  // the current absolute time falls into
  const isPortrait = compHeight > compWidth;
  const cropPosition = useMemo(() => {
    if (!isPortrait || !cropCenterKeyframes || cropCenterKeyframes.length === 0) {
      return null;
    }

    const resolveCropAtFrame = (f: number) => {
      const absoluteTime = (!isVideoLocked && fullVideoDuration)
        ? f / fps
        : outputFrameToSourceFrame(sceneLayout.blocks, f) / fps;
      // Same rule as the export (covering, else hold the previous, else the
      // first), so time outside every keyframe previews what will render.
      const kf: { x: number; y: number; zoom?: number } | null =
        resolveCropKeyframeAtTime(cropCenterKeyframes, absoluteTime);
      if (!kf) return null;
      return { x: kf.x, y: kf.y, zoom: typeof kf.zoom === 'number' && kf.zoom > 0 ? kf.zoom : 1 };
    };
    // Camera-move tween only applies on the locked-video axis, where cut
    // windows are in output-frame coordinates. In the unlocked path the frame
    // axis is the full source video, so the cut windows don't map — skip it.
    const usesFullVideoAxis = !isVideoLocked && !!fullVideoDuration;
    return (
      (usesFullVideoAxis
        ? null
        : resolveCameraMoveCrop({ frame, cuts: [...sceneLayout.cuts, ...keyframeCuts], resolveAt: resolveCropAtFrame }))
        ?? resolveCropAtFrame(frame)
    );
  }, [isPortrait, cropCenterKeyframes, frame, fps, isVideoLocked, fullVideoDuration, sceneLayout, keyframeCuts]);

  // Helper function to convert seconds to time string format
  const formatTimeString = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const wholeSecs = Math.floor(secs);
    const millisecs = Math.floor((secs - wholeSecs) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${wholeSecs.toString().padStart(2, '0')}.${millisecs.toString().padStart(3, '0')}`;
  };

  // Convert ProjectPreview data format to reorganize-words library format
  const convertToLibraryFormat = useCallback((segments: any[]) => {
    const librarySegments = segments.map(segment => ({
      startTime: segment.startTime,
      endTime: segment.endTime,
      words: segment.words.map((word: any) => ({
        word: word.word,
        startTime: word.startTime,
        endTime: word.endTime,
      })),
    }));

    return {
      subtitle: {
        transcription: {
          segments: librarySegments
        }
      }
    };
  }, []);

  // Convert reorganized segments back to ProjectPreview format
  const convertFromLibraryFormat = useCallback((reorganizedSegments: any[], fps: number) => {
    return reorganizedSegments.map((segment) => {
      const start = segment.startTime.split(':').reduce((acc: number, val: string, i: number) => {
        if (i === 0) return parseFloat(val) * 3600;
        if (i === 1) return acc + parseFloat(val) * 60;
        return acc + parseFloat(val.replace(',', '.'));
      }, 0);

      const end = segment.endTime.split(':').reduce((acc: number, val: string, i: number) => {
        if (i === 0) return parseFloat(val) * 3600;
        if (i === 1) return acc + parseFloat(val) * 60;
        return acc + parseFloat(val.replace(',', '.'));
      }, 0);

      return {
        startTime: segment.startTime,
        endTime: segment.endTime,
        start,
        end,
        startFrame: Math.round(start * fps),
        endFrame: Math.round(end * fps),
        words: segment.words.map((word: any, wordIndex: number) => {
          const wordStart = word.startTime.split(':').reduce((acc: number, val: string, i: number) => {
            if (i === 0) return parseFloat(val) * 3600;
            if (i === 1) return acc + parseFloat(val) * 60;
            return acc + parseFloat(val.replace(',', '.'));
          }, 0);

          const wordEnd = word.endTime.split(':').reduce((acc: number, val: string, i: number) => {
            if (i === 0) return parseFloat(val) * 3600;
            if (i === 1) return acc + parseFloat(val) * 60;
            return acc + parseFloat(val.replace(',', '.'));
          }, 0);

          return {
            ...word,
            start: wordStart,
            end: wordEnd,
            startFrame: Math.round(wordStart * fps),
            endFrame: Math.round(wordEnd * fps),
            startTime: word.startTime,
            endTime: word.endTime,
            wordIndex,
          };
        }),
      };
    });
  }, []);

  // Transform transcription data to match Subtitle component format
  const subtitleData = useMemo(() => {
    if (!transcriptionSegments) return null;

    let wordIndex = 0;
    const rawSegments = transcriptionSegments.flatMap(segmentGroup =>
      segmentGroup.map(segment => {
        let segmentCompositionStart, segmentCompositionEnd;

        if (!isVideoLocked) {
          // When unlocked, use absolute time directly
          segmentCompositionStart = segment.startTime;
          segmentCompositionEnd = segment.endTime;
        } else {
          // When locked, convert absolute time to composition time using intervals
          if (sceneItems.length === 0) return null;

          // Helper function to convert absolute time to composition time
          const absoluteToCompositionTime = (absoluteTime: number): number =>
            sourceTimeToOutputTime(sceneItems, sceneLayout.blocks, absoluteTime, fps);

          segmentCompositionStart = absoluteToCompositionTime(segment.startTime);
          segmentCompositionEnd = absoluteToCompositionTime(segment.endTime);
        }

        return {
          startTime: formatTimeString(segmentCompositionStart),
          endTime: formatTimeString(segmentCompositionEnd),
          start: segmentCompositionStart,
          end: segmentCompositionEnd,
          startFrame: Math.round(segmentCompositionStart * fps),
          endFrame: Math.round(segmentCompositionEnd * fps),
          words: segment.words
            .filter(word => {
              // Sidebar context includes speech outside the cuts. Mapping those
              // words to clip time would clamp them onto the first/last frame
              // and mix unrelated speech into the rendered subtitle lines.
              // Check real bounds: silent intervals can promote a context word
              // for editing without making that word part of the video.
              const start = word.start ?? word.start_time ?? word.startTime ?? 0;
              const end = word.end ?? word.end_time ?? word.endTime ?? start;
              return keptIntervals.some(interval => start < interval.endTime && end > interval.startTime);
            })
            .map(word => {
              const wordStartTime = word.start || word.start_time || word.startTime || 0;
              const wordEndTime = word.end || word.end_time || word.endTime || wordStartTime;

              let wordCompositionStart, wordCompositionEnd;

              if (!isVideoLocked) {
                // When unlocked, use absolute time directly
                wordCompositionStart = wordStartTime;
                wordCompositionEnd = wordEndTime;
              } else {
                // When locked, convert absolute time to composition time using intervals
                const absoluteToCompositionTime = (absoluteTime: number): number =>
                  sourceTimeToOutputTime(sceneItems, sceneLayout.blocks, absoluteTime, fps);

                wordCompositionStart = absoluteToCompositionTime(wordStartTime);
                wordCompositionEnd = absoluteToCompositionTime(wordEndTime);
              }

              return {
                word: word.word,
                startTime: formatTimeString(wordCompositionStart),
                endTime: formatTimeString(wordCompositionEnd),
                start: wordCompositionStart,
                end: wordCompositionEnd,
                startFrame: Math.round(wordCompositionStart * fps),
                endFrame: Math.round(wordCompositionEnd * fps),
                wordIndex: wordIndex++,
              };
            }),
        };
      })
    ).filter(segment => segment !== null && segment.words.length > 0);

    // Apply word reorganization using library function
    const projectData = convertToLibraryFormat(rawSegments);
    const reorganizeOptions = buildSubtitleReorganizeOptions({
      maxWordsPerLine: customSubtitleConfig?.maxWordsPerLine || 4,
      removePunctuation: customSubtitleConfig?.removePunctuation,
    });

    const libraryReorganizedSegments = reorganizeWords(projectData, reorganizeOptions);
    const reorganizedSegments = convertFromLibraryFormat(libraryReorganizedSegments, fps);

    return { segments: reorganizedSegments };
  }, [transcriptionSegments, keptIntervals, sceneItems, sceneLayout, fps, convertToLibraryFormat, convertFromLibraryFormat, isVideoLocked, customSubtitleConfig?.maxWordsPerLine, customSubtitleConfig?.removePunctuation]);

  // Forbidden attributes that cannot be changed (same as SubtitlePresetComposition)
  const FORBIDDEN_ATTRIBUTES = new Set([
    'project.subtitle.lineStyle.fontSize',
    'project.subtitle.lineStyle.verticalMargin',
    'project.subtitle.lineStyle.horizontalMargin',
    'project.subtitle.lineStyle.justifyContent',
    'project.subtitle.lineStyle.alignItems',
  ]);

  const isForbiddenAttribute = (attribute: string): boolean => FORBIDDEN_ATTRIBUTES.has(attribute);

  // Create subtitle style configuration using preset application (same as SubtitlePresetComposition)
  const subtitleConfig = useMemo(() => {
    if (!subtitleData || !subtitlesEnabled) return null;

    const baseStyles = {
      highlightStyle: {
        fontColor: '#0DFDE8',
      },
      minSilenceToCut: 0.5,
      highlightBoxStyle: {
        enabled: false,
        paddingX: 0.3,
        paddingY: 0.05,
        borderRadius: 0.2,
        backgroundColor: '#4023d1',
      },
      keepOnlySubtitles: true,
      extraTimeTolerance: 0.75,
      minSubtitleSilenceToCut: 0.5,
      animation: null,
      lineStyle: {
        padding: 0.5,
        fontName: 'Komika Title Axis',
        fontColor: '#ebe9f1',
        lineHeight: 1,
        shadowSize: 11,
        shadowColor: '#050505',
        borderRadius: 0.25,
        textTransform: 'uppercase',
        backgroundColor: 'black',
        enableBackground: false,
        highlightCurrentWord: false,
        fontSize: 48,
        verticalMarginPercentage: 15,
        horizontalMarginPercentage: 10,
        paddingX: 0.3,
        paddingY: 0.5,
        gap: 0.3,
        justifyContent: 'center',
        alignItems: 'flex-end',
      },
      transcription: subtitleData,
    };

    // Apply preset changes if selectedSubtitlePreset is provided
    if (selectedSubtitlePreset?.preset) {
      for (const { type, attribute, value } of selectedSubtitlePreset.preset) {
        if (attribute && isForbiddenAttribute(attribute)) {
          continue;
        }

        if (type === 'set-value' && attribute) {
          lodash.set(baseStyles, attribute.replace('project.subtitle.', ''), value);
        } else if (type === 'reorganize-words') {
          // Word reorganization is already handled in our subtitleData transformation
          lodash.set(baseStyles, attribute || '', value);
        }
      }
    }

    // Apply custom subtitle config if provided (overrides preset)
    if (customSubtitleConfig) {
      if (customSubtitleConfig.animation) {
        baseStyles.animation = customSubtitleConfig.animation;
      }
      if (customSubtitleConfig.fontFamily) {
        baseStyles.lineStyle.fontName = customSubtitleConfig.fontFamily;
      }
      if (customSubtitleConfig.fontUrl) {
        (baseStyles.lineStyle as Record<string, unknown>).fontUrl = customSubtitleConfig.fontUrl;
      }
      if (customSubtitleConfig.fontSize) {
        baseStyles.lineStyle.fontSize = customSubtitleConfig.fontSize;
      }
      if (customSubtitleConfig.textColor) {
        baseStyles.lineStyle.fontColor = customSubtitleConfig.textColor;
      }
      if (customSubtitleConfig.highlightColor) {
        baseStyles.highlightStyle.fontColor = customSubtitleConfig.highlightColor;
      }
      if (customSubtitleConfig.highlightEnabled !== undefined) {
        baseStyles.lineStyle.highlightCurrentWord = customSubtitleConfig.highlightEnabled;
      }
      if (customSubtitleConfig.highlightBoxEnabled !== undefined) {
        baseStyles.highlightBoxStyle.enabled = customSubtitleConfig.highlightBoxEnabled;
      }
      if (customSubtitleConfig.highlightBoxBackgroundColor) {
        baseStyles.highlightBoxStyle.backgroundColor = customSubtitleConfig.highlightBoxBackgroundColor;
      }
      if (customSubtitleConfig.highlightBoxBorderRadius !== undefined) {
        baseStyles.highlightBoxStyle.borderRadius = customSubtitleConfig.highlightBoxBorderRadius;
      }
      if (customSubtitleConfig.highlightBoxPaddingX !== undefined) {
        baseStyles.highlightBoxStyle.paddingX = customSubtitleConfig.highlightBoxPaddingX;
      }
      if (customSubtitleConfig.highlightBoxPaddingY !== undefined) {
        baseStyles.highlightBoxStyle.paddingY = customSubtitleConfig.highlightBoxPaddingY;
      }
      if (customSubtitleConfig.backgroundColor) {
        baseStyles.lineStyle.backgroundColor = customSubtitleConfig.backgroundColor;
      }
      if (customSubtitleConfig.backgroundEnabled !== undefined) {
        baseStyles.lineStyle.enableBackground = customSubtitleConfig.backgroundEnabled;
      }
      // Background box size: percent scales of the base paddings. Without
      // these two mappings the Fundo width/height sliders changed nothing.
      if (customSubtitleConfig.backgroundWidth !== undefined) {
        (baseStyles.lineStyle as Record<string, unknown>).backgroundWidthPct = customSubtitleConfig.backgroundWidth;
      }
      if (customSubtitleConfig.backgroundHeight !== undefined) {
        (baseStyles.lineStyle as Record<string, unknown>).backgroundHeightPct = customSubtitleConfig.backgroundHeight;
      }
      if (customSubtitleConfig.backgroundOpacity !== undefined && customSubtitleConfig.backgroundEnabled) {
        // The opacity rides ON THE COLOR — this used to only toggle
        // enableBackground, so the slider never changed anything visible.
        baseStyles.lineStyle.enableBackground = customSubtitleConfig.backgroundOpacity > 0;
        const hex = baseStyles.lineStyle.backgroundColor;
        if (typeof hex === 'string' && hex.startsWith('#')) {
          baseStyles.lineStyle.backgroundColor = hexToRgba(hex, customSubtitleConfig.backgroundOpacity / 100);
        }
      }
      if (customSubtitleConfig.shadowEnabled !== undefined) {
        if (customSubtitleConfig.shadowEnabled) {
          baseStyles.lineStyle.shadowSize = customSubtitleConfig.shadowSize || 11;
          baseStyles.lineStyle.shadowColor = customSubtitleConfig.shadowColor || '#050505';
        } else {
          baseStyles.lineStyle.shadowSize = 0;
        }
      }
      Object.assign(baseStyles.lineStyle, subtitleOutlineLineStyleFromPartial(customSubtitleConfig));
      if (customSubtitleConfig.lineSpacing) {
        baseStyles.lineStyle.lineHeight = customSubtitleConfig.lineSpacing;
      }
      if (customSubtitleConfig.textAlign) {
        // Map text alignment to justify content
        const alignmentMap: { [key: string]: string } = {
          'left': 'flex-start',
          'center': 'center',
          'right': 'flex-end'
        };
        baseStyles.lineStyle.justifyContent = alignmentMap[customSubtitleConfig.textAlign] || 'center';
      }
      if (customSubtitleConfig.textTransform) {
        baseStyles.lineStyle.textTransform = customSubtitleConfig.textTransform;
      }
      if (customSubtitleConfig.position) {
        // Map position to align items
        const positionMap: { [key: string]: string } = {
          'top-center': 'flex-start',
          'middle-center': 'center',
          'bottom-center': 'flex-end'
        };
        baseStyles.lineStyle.alignItems = positionMap[customSubtitleConfig.position] || 'flex-end';
      }
      if (customSubtitleConfig.marginVertical !== undefined) {
        baseStyles.lineStyle.verticalMarginPercentage = customSubtitleConfig.marginVertical;
      }
      if (customSubtitleConfig.marginHorizontal !== undefined) {
        baseStyles.lineStyle.horizontalMarginPercentage = customSubtitleConfig.marginHorizontal;
      }
    }

    return baseStyles;
  }, [subtitleData, subtitlesEnabled, selectedSubtitlePreset, customSubtitleConfig]);

  // Helper: get panel layouts for split-speaker mode
  const getSplitPanels = (numSpeakers: number) => {
    const gap = 4;
    switch (numSpeakers) {
      case 2: {
        const h = (compHeight - gap) / 2;
        return [
          { x: 0, y: 0, width: compWidth, height: h },
          { x: 0, y: h + gap, width: compWidth, height: h },
        ];
      }
      case 3: {
        const topH = (compHeight - gap) / 2;
        const botH = compHeight - topH - gap;
        const halfW = (compWidth - gap) / 2;
        return [
          { x: 0, y: 0, width: compWidth, height: topH },
          { x: 0, y: topH + gap, width: halfW, height: botH },
          { x: halfW + gap, y: topH + gap, width: halfW, height: botH },
        ];
      }
      case 4: {
        const halfW = (compWidth - gap) / 2;
        const halfH = (compHeight - gap) / 2;
        return [
          { x: 0, y: 0, width: halfW, height: halfH },
          { x: halfW + gap, y: 0, width: halfW, height: halfH },
          { x: 0, y: halfH + gap, width: halfW, height: halfH },
          { x: halfW + gap, y: halfH + gap, width: halfW, height: halfH },
        ];
      }
      default:
        return [{ x: 0, y: 0, width: compWidth, height: compHeight }];
    }
  };

  // Helper: find speaker crop position for a given absolute time
  const getSpeakerCropAt = (speaker: { scenes: Array<{ start: number; end: number; cropCenter: { x: number; y: number } }> }, absoluteTime: number) => {
    for (const scene of speaker.scenes) {
      if (absoluteTime >= scene.start && absoluteTime <= scene.end && scene.cropCenter) {
        return scene.cropCenter;
      }
    }
    return speaker.scenes[0]?.cropCenter || { x: 50, y: 50 };
  };

  // Determine absolute time for speaker layout panels
  const absoluteTimeForSpeaker = useMemo(() => {
    if (!speakerLayout || speakerLayout.numSpeakers <= 1) return 0;
    return outputFrameToSourceFrame(sceneLayout.blocks, frame) / fps;
  }, [speakerLayout, sceneLayout, frame, fps]);

  // Same-face dedup constants/helpers — kept aligned with the render-side
  // logic in src/remotion/SplitSpeakerComposition.tsx so the live preview
  // shows the same number of panels the rendered video will have.
  const MERGE_DISTANCE_THRESHOLD = 20;

  // Pick which speakers to render in the multi-head layout. Priority:
  //   1. Speakers whose face_id is present in the current scene (according to sceneTimeline).
  //   2. Top-by-totalTime speakers, used to pad up to numSpeakers when fewer
  //      faces are in the current scene than the user requested.
  // The result always has exactly `numSpeakers` entries (or the full list of
  // available speakers if the user has detected fewer than they asked for).
  const activeSpeakers = useMemo(() => {
    if (!speakerLayout || speakerLayout.numSpeakers <= 1 || speakerLayout.speakers.length <= 1) return [];

    const target = Math.min(speakerLayout.numSpeakers, speakerLayout.speakers.length);

    // Pick the face_ids present in the current scene, in the order given by
    // the top-by-totalTime ranking.
    let active: typeof speakerLayout.speakers = [];
    const timeline = speakerLayout.sceneTimeline;
    if (timeline && timeline.length > 0) {
      const currentScene = timeline.find(s => absoluteTimeForSpeaker >= s.start && absoluteTimeForSpeaker <= s.end);
      if (currentScene) {
        const presentFaceIds = new Set(currentScene.faceIds);
        active = speakerLayout.speakers.filter(speaker => presentFaceIds.has(speaker.face_id));
      }
    } else {
      active = speakerLayout.speakers.filter(speaker =>
        speaker.scenes.some((scene: { start: number; end: number }) => absoluteTimeForSpeaker >= scene.start && absoluteTimeForSpeaker <= scene.end)
      );
    }

    // Pad up to `target` with the next top-by-totalTime speakers not already selected.
    // Skip candidates whose crop center (at the current playhead) is within
    // MERGE_DISTANCE_THRESHOLD of any already-selected speaker — per-axis
    // comparison, matching the render-side deduplicateCloseSpeakers logic in
    // src/remotion/SplitSpeakerComposition.tsx.
    const selectedIds = new Set(active.map(s => s.face_id));
    const padded = active.slice(0, target);
    // Crop centers of the speakers already in `padded`, sampled at the current playhead.
    const paddedCenters: Array<{ x: number; y: number }> = padded.map(s =>
      getSpeakerCropAt(s, absoluteTimeForSpeaker)
    );

    for (const speaker of speakerLayout.speakers) {
      if (padded.length >= target) break;
      if (selectedIds.has(speaker.face_id)) continue;
      const candidate = getSpeakerCropAt(speaker, absoluteTimeForSpeaker);
      const tooClose = paddedCenters.some(c =>
        Math.abs(c.x - candidate.x) < MERGE_DISTANCE_THRESHOLD &&
        Math.abs(c.y - candidate.y) < MERGE_DISTANCE_THRESHOLD
      );
      if (tooClose) continue;
      padded.push(speaker);
      selectedIds.add(speaker.face_id);
      paddedCenters.push(candidate);
    }

    return padded;
  }, [speakerLayout, absoluteTimeForSpeaker]);

  const isSplitSpeaker = speakerLayout && speakerLayout.numSpeakers > 1 && speakerLayout.speakers.length > 1;

  // Context clip-mirror layers need the same source video + seconds-mode
  // intervals as the rest of this preview (it works in source-seconds, not
  // frames), so the mirror stays frame-synced across both render branches
  // below (post-layout and default).
  const layerClipContext = useMemo(() => ({ videoUrl, intervals: sceneItems, playback, previewPlaying }), [videoUrl, sceneItems, playback, previewPlaying]);

  // Post-frame layout — dispatch to the registry renderer, slotting the embedded media
  if (postLayout) {
    const def = POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS];
    if (def) {
      const buildClip = (muted: boolean) => (
        <ClipVideoSequences
          videoUrl={videoUrl}
          intervals={sceneItems}
          fps={fps}
          isMuted={muted || isMuted}
          volume={muted ? 0 : volume}
          objectFit={def.mediaFit}
          cropPosition={cropPosition}
          transitions={previewTransitions}
          playback={playback}
          onMediaError={onMediaError}
          previewPlaying={previewPlaying}
          onPreviewBuffering={onPreviewBuffering}
          onPreviewMediaReady={onPreviewMediaReady}
        />
      );
      const media = resolveMediaMode(def.mode, postLayout as { imageUrl?: string }) === 'static_image'
        ? (
            <StaticImageMedia
              imageUrl={(postLayout as { imageUrl?: string }).imageUrl}
              audio={<ClipAudioSequences videoUrl={videoUrl} fps={fps} intervals={sceneItems} isMuted={isMuted} volume={volume} transitions={previewTransitions} />}
            />
          )
        : buildClip(false);
      const renderClip = () => {
        if (resolveLayoutClipCopyMode(layoutClipCopyHost) === 'still') {
          return posterUrl ? (
            <EffectsImage src={posterUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div data-testid="layout-clip-still" style={{ width: '100%', height: '100%', backgroundColor: '#000' }} />
          );
        }
        return buildClip(true);
      };
      const Renderer = def.Renderer;
      // Logo, watermark, and subtitles render ABOVE the post-frame layout, with
      // the same conditions as the standard path below — selecting a post layout
      // must not drop the configured logo, the (possibly campaign-enforced)
      // watermark, or the subtitles.
      return (
        <AbsoluteFill>
          <Renderer config={postLayout} media={media} renderClip={renderClip} />

          {logoConfig && logoConfig.url && (
            <Logo
              url={logoConfig.url}
              position={logoConfig.position}
              size={logoConfig.size}
              opacity={logoConfig.opacity}
              horizontalMargin={logoConfig.horizontalMargin}
              verticalMargin={logoConfig.verticalMargin}
            />
          )}

          {/* Media layers (image overlays) authored in the editor — same rendering as
              the default path below, so post-layouts (e.g. satisfatorio) stay WYSIWYG
              with the render path (PostFrameComposition's front overlays). */}
          {(layers ?? []).map((overlay, i) => (
            <Sequence
              key={`layer-${i}`}
              from={Math.round(overlay.start * fps)}
              durationInFrames={Math.max(1, Math.round((overlay.end - overlay.start) * fps))}
            >
              <LayerOverlay overlay={overlay} clipContext={layerClipContext} />
            </Sequence>
          ))}

          <ClipWatermark campaignWatermark={campaignWatermark} showWatermark={showWatermark} position={watermarkPosition} />
          {subtitleConfig && <Subtitle subtitle={subtitleConfig} curve={circuloCurveFromLayout(postLayout)} />}
        </AbsoluteFill>
      );
    }
  }

  return (
    <AbsoluteFill>
      {isSplitSpeaker ? (() => {
        // Split-speaker layout rendering — use active speakers count for dynamic layout
        const effectiveCount = activeSpeakers.length;
        const panels = getSplitPanels(effectiveCount);

        return panels.map((panel, panelIndex) => {
          const speaker = activeSpeakers[panelIndex];
          if (!speaker) return null;

          const speakerCrop = getSpeakerCropAt(speaker, absoluteTimeForSpeaker);
          const SOURCE_ASPECT = sourceAspectRatio;
          const panelAspect = panel.width / panel.height;
          const scaleX = panelAspect < SOURCE_ASPECT ? SOURCE_ASPECT / panelAspect : 1;
          const scaleY = panelAspect > SOURCE_ASPECT ? panelAspect / SOURCE_ASPECT : 1;
          const panelLeft = scaleX > 1
            ? Math.max(-(scaleX - 1) * 100, Math.min(0, 50 - scaleX * speakerCrop.x))
            : 0;
          const panelTop = scaleY > 1
            ? Math.max(-(scaleY - 1) * 100, Math.min(0, 50 - scaleY * speakerCrop.y))
            : 0;

          return (
            <div
              key={speaker.face_id}
              data-testid="speaker-panel"
              style={{
                position: 'absolute',
                left: panel.x,
                top: panel.y,
                width: panel.width,
                height: panel.height,
                overflow: 'hidden',
                borderRadius: 8,
              }}
            >
              {playback === 'html5' ? (
                <EditorPlaybackVideo
                  videoUrl={videoUrl}
                  sourceTime={resolveEditorPlaybackFrame({
                    frame,
                    blocks: usesFullVideoAxis && fullVideoDuration
                      ? [{ from: 0, durationInFrames: Math.max(1, Math.round(fullVideoDuration * fps)), trimBefore: 0, trimAfter: Math.round(fullVideoDuration * fps) }]
                      : sceneLayout.blocks,
                    cuts: usesFullVideoAxis ? [] : sceneLayout.cuts,
                    fps,
                    baseVolume: isMuted ? 0 : volume,
                  }).primary.sourceTime}
                  playing={previewPlaying}
                  volume={panelIndex === 0 && !isMuted ? volume : 0}
                  muted={!!isMuted || panelIndex !== 0}
                  objectFit="fill"
                  reportBuffering={panelIndex === 0}
                  onBufferingChange={panelIndex === 0 ? onPreviewBuffering : undefined}
                  onReady={panelIndex === 0 ? onPreviewMediaReady : undefined}
                  onMediaError={onMediaError}
                />
              ) : sceneLayout.blocks.map((block, index) => {
                if (block.durationInFrames <= 0) return null;
                const fades = sceneAudioFades(sceneLayout.cuts, index);
                const vol = panelIndex === 0
                  ? makeSceneVolume({
                      baseVolume: isMuted ? 0 : volume,
                      fadeInFrames: fades.fadeInFrames,
                      fadeOutFrames: fades.fadeOutFrames,
                      durationInFrames: block.durationInFrames,
                    })
                  : 0;

                return (
                  <Sequence key={index} from={block.from} durationInFrames={block.durationInFrames} premountFor={150}>
                    <div
                      style={{
                        position: 'absolute',
                        width: `${scaleX * 100}%`,
                        height: `${scaleY * 100}%`,
                        left: `${panelLeft}%`,
                        top: `${panelTop}%`,
                      }}
                    >
                      <ClipPlaybackVideo
                        videoUrl={videoUrl}
                        trimBefore={block.trimBefore}
                        trimAfter={block.trimAfter}
                        fps={fps}
                        volume={vol}
                        isMuted={isMuted}
                        objectFit="fill"
                        playback={playback}
                        onMediaError={onMediaError}
                      />
                    </div>
                  </Sequence>
                );
              })}
            </div>
          );
        });
      })() : (() => {
        // Original single-video rendering
        // Same geometry as the export (RenderComposition), zoom included.
        const cropBox = computeCropWrapperBox({
          cropPosition,
          sourceAspect: sourceAspectRatio,
          compAspect: compWidth / compHeight,
        });

        const videoStyle: React.CSSProperties = cropPosition
          ? { width: "100%", height: "100%", ...style }
          : { width: "100%", height: "100%", objectFit: "contain" as const, ...style };

        const renderFaceBoxes = () => {
          if (!cropPosition || !activeFaces || activeFaces.length === 0) return null;
          return activeFaces.map((face, index) => {
            const color = face.is_main_speaker
              ? '#22C55E'
              : face.is_speaking
                ? '#EAB308'
                : '#EF4444';
            return (
              <div
                key={`${face.face_id}-${index}`}
                style={{
                  position: 'absolute',
                  left: `${face.x * 100}%`,
                  top: `${face.y * 100}%`,
                  width: `${face.width * 100}%`,
                  height: `${face.height * 100}%`,
                  border: `2px solid ${color}`,
                  borderRadius: '4px',
                  boxSizing: 'border-box' as const,
                  pointerEvents: 'none' as const,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '-18px',
                    left: '0',
                    background: color,
                    color: '#000',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    whiteSpace: 'nowrap' as const,
                  }}
                >
                  {face.face_id}{face.is_main_speaker ? ' (main)' : ''}{face.is_speaking ? ' [speaking]' : ''}
                </span>
              </div>
            );
          });
        };

        if (playback === 'html5') {
          const playbackBlocks = (!isVideoLocked && fullVideoDuration)
            ? [{
                from: 0,
                durationInFrames: Math.max(1, Math.round(fullVideoDuration * fps)),
                trimBefore: 0,
                trimAfter: Math.round(fullVideoDuration * fps),
              }]
            : sceneLayout.blocks;
          const playbackCuts = (!isVideoLocked && fullVideoDuration) ? [] : sceneLayout.cuts;
          const place = (video: React.ReactNode, faces: boolean) => cropPosition ? (
            <div style={{ position: 'absolute', width: `${cropBox.widthPct}%`, height: `${cropBox.heightPct}%`, left: `${cropBox.leftPct}%`, top: `${cropBox.topPct}%` }}>
              {video}
              {faces ? renderFaceBoxes() : null}
            </div>
          ) : video;
          return (
            <>
              <KeyframeTransitionStyles cuts={keyframeCuts}>
                <EditorPlaybackStack
                  videoUrl={videoUrl}
                  blocks={playbackBlocks}
                  cuts={playbackCuts}
                  fps={fps}
                  baseVolume={isMuted ? 0 : volume}
                  muted={!!isMuted}
                  playing={previewPlaying}
                  objectFit={cropPosition ? 'fill' : 'contain'}
                  onBufferingChange={onPreviewBuffering}
                  onReady={onPreviewMediaReady}
                  onMediaError={onMediaError}
                  renderPrimary={(video) => place(video, true)}
                  renderIncoming={(video) => place(video, false)}
                />
              </KeyframeTransitionStyles>
              <TransitionDipOverlays cuts={[...playbackCuts, ...((!isVideoLocked && fullVideoDuration) ? [] : keyframeCuts)]} />
            </>
          );
        }

        if (!isVideoLocked && fullVideoDuration) {
          const sfrom = 0;
          const eat = Math.round(fullVideoDuration * fps);
          const dur = Math.max(0, eat - sfrom);

          const videoEl = (
            <ClipPlaybackVideo
              videoUrl={videoUrl}
              trimBefore={sfrom}
              trimAfter={eat}
              fps={fps}
              volume={isMuted ? 0 : volume}
              isMuted={isMuted}
              objectFit={cropPosition ? 'fill' : 'contain'}
              playback={playback}
              style={videoStyle}
              onMediaError={onMediaError}
            />
          );

          return (
            <Sequence key={0} from={0} durationInFrames={dur} premountFor={150}>
              {cropPosition ? (
                <div style={{ position: 'absolute', width: `${cropBox.widthPct}%`, height: `${cropBox.heightPct}%`, left: `${cropBox.leftPct}%`, top: `${cropBox.topPct}%` }}>
                  {videoEl}
                  {renderFaceBoxes()}
                </div>
              ) : videoEl}
            </Sequence>
          );
        }

        return (
          <>
            <KeyframeTransitionStyles cuts={keyframeCuts}>
              {sceneLayout.blocks.map((block, index) => {
                if (block.durationInFrames <= 0) return null;
                const fades = sceneAudioFades(sceneLayout.cuts, index);
                const vol = makeSceneVolume({
                  baseVolume: isMuted ? 0 : volume,
                  fadeInFrames: fades.fadeInFrames,
                  fadeOutFrames: fades.fadeOutFrames,
                  durationInFrames: block.durationInFrames,
                });
                const videoEl = (
                  <ClipPlaybackVideo
                    videoUrl={videoUrl}
                    trimBefore={block.trimBefore}
                    trimAfter={block.trimAfter}
                    fps={fps}
                    volume={vol}
                    isMuted={isMuted}
                    objectFit={cropPosition ? 'fill' : 'contain'}
                    playback={playback}
                    style={videoStyle}
                    onMediaError={onMediaError}
                  />
                );
                return (
                  <Sequence key={index} from={block.from} durationInFrames={block.durationInFrames} premountFor={150}>
                    <SceneTransitionWrapper cuts={sceneLayout.cuts} sceneIndex={index} blockFrom={block.from}>
                      {cropPosition ? (
                        <div style={{ position: 'absolute', width: `${cropBox.widthPct}%`, height: `${cropBox.heightPct}%`, left: `${cropBox.leftPct}%`, top: `${cropBox.topPct}%` }}>
                          {videoEl}
                          {renderFaceBoxes()}
                        </div>
                      ) : videoEl}
                    </SceneTransitionWrapper>
                  </Sequence>
                );
              })}
            </KeyframeTransitionStyles>
            <TransitionDipOverlays cuts={[...sceneLayout.cuts, ...keyframeCuts]} />
          </>
        );
      })()}

      {/* Logo overlay */}
      {logoConfig && logoConfig.url && (
        <Logo
          url={logoConfig.url}
          position={logoConfig.position}
          size={logoConfig.size}
          opacity={logoConfig.opacity}
          horizontalMargin={logoConfig.horizontalMargin}
          verticalMargin={logoConfig.verticalMargin}
        />
      )}

      {/* Media layers (image overlays) authored in the editor */}
      {(layers ?? []).map((overlay, i) => (
        <Sequence
          key={`layer-${i}`}
          from={Math.round(overlay.start * fps)}
          durationInFrames={Math.max(1, Math.round((overlay.end - overlay.start) * fps))}
        >
          <LayerOverlay overlay={overlay} clipContext={layerClipContext} />
        </Sequence>
      ))}

      {/* Watermark overlay for free-plan users or campaign watermark */}
      <ClipWatermark campaignWatermark={campaignWatermark} showWatermark={showWatermark} position={watermarkPosition} />

      {/* Subtitle overlay using existing Subtitle component */}
      {subtitleConfig && (
        <Subtitle subtitle={subtitleConfig} curve={circuloCurveFromLayout(postLayout)} />
      )}

      {/* Safe Zone overlay - Only for portrait mode */}
      {showSafeZone && (
        <AbsoluteFill style={{ pointerEvents: 'none' }}>
          <img
            src="/images/safe-zone-9-16.png"
            alt="Safe Zone 9:16"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: 0.7,
              mixBlendMode: 'overlay' as any,
            }}
          />
        </AbsoluteFill>
      )}

    </AbsoluteFill>
  );
};

export const ProjectClipComposition: React.FC<React.ComponentProps<typeof ProjectClipCompositionInner>> = (props) => (
  <VideoEffectsProvider
    value={props.videoEffects}
    mapTime={!props.isVideoLocked && !!props.fullVideoDuration
      ? (t) => mapSourceTimeToClipTime(t, resolveKeptIntervals(props.intervals, props.startTime, props.endTime))
      : undefined}
  ><ProjectClipCompositionInner {...props} /></VideoEffectsProvider>
);
